import { getBackendUrl } from "../config";
import { getToken } from "../services/tokenStore";

// Áudio da ligação de WhatsApp entre o navegador e o motor, por WebSocket, na
// mesma porta HTTPS do sistema (não depende de UDP nem de porta extra).
//
// Dois formatos, negociados no subprotocolo do WebSocket:
//   "opus"  — o navegador comprime com WebCodecs (~32 kbps por sentido).
//   "pcm16" — áudio cru 16 kHz (~256 kbps), para navegador sem WebCodecs.
//
// A captura roda num AudioWorklet, fora da thread da tela: com a interface
// ocupada (lista grande, troca de tela) a voz não picota. Onde não houver
// AudioWorklet, cai no ScriptProcessor.
//
// O microfone entra a 48 kHz direto no Opus. No modo PCM (reserva) a redução
// para 16 kHz usa filtro FIR: média simples abafava a voz.

const TAXA_ENVIO = 16000;
const AMOSTRAS_QUADRO = 320; // 20 ms a 16 kHz
const TAXA_OPUS_RETORNO = 48000; // o motor codifica o retorno a 48 kHz
const FOLGA_INICIAL = 0.08; // 80 ms de colchão contra rede irregular

const CODIGO_WORKLET = `
class CapturaLigacao extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Float32Array(512); this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      for (let i = 0; i < ch.length; i++) {
        this.buf[this.n++] = ch[i];
        if (this.n === this.buf.length) { this.port.postMessage(this.buf.slice(0)); this.n = 0; }
      }
    }
    return true;
  }
}
registerProcessor("captura-ligacao", CapturaLigacao);
`;

const urlDoAudio = (id) => {
	const base = String(getBackendUrl() || window.location.origin).replace(/\/+$/, "");
	const ws = base.replace(/^https:/, "wss:").replace(/^http:/, "ws:");
	return `${ws}/calls/${id}/audio?token=${encodeURIComponent(getToken() || "")}`;
};

// Passa-baixa FIR (sinc janelado, corte ~7,2 kHz a 48 kHz) para reduzir
// 48k→16k sem abafar: a média de 3 amostras comia os agudos da fala.
const FIR_48K = (() => {
	const taps = 31;
	const corte = 7200 / 48000;
	const meio = (taps - 1) / 2;
	const h = [];
	let soma = 0;
	for (let n = 0; n < taps; n += 1) {
		const x = n - meio;
		const sinc = x === 0 ? 2 * corte : Math.sin(2 * Math.PI * corte * x) / (Math.PI * x);
		const janela = 0.54 - 0.46 * Math.cos((2 * Math.PI * n) / (taps - 1));
		h.push(sinc * janela);
		soma += sinc * janela;
	}
	return h.map((v) => v / soma);
})();

const criarRedutor = (taxaOrigem) => {
	// Guarda o fim do bloco anterior para o filtro não "estalar" na emenda.
	let historico = new Float32Array(FIR_48K.length);
	return (entrada) => {
		if (taxaOrigem === TAXA_ENVIO) return entrada;
		if (taxaOrigem === 48000) {
			const buf = new Float32Array(historico.length + entrada.length);
			buf.set(historico);
			buf.set(entrada, historico.length);
			const saida = new Float32Array(Math.floor(entrada.length / 3));
			for (let i = 0; i < saida.length; i += 1) {
				const base = historico.length + i * 3;
				let acc = 0;
				for (let k = 0; k < FIR_48K.length; k += 1) acc += FIR_48K[k] * buf[base - k];
				saida[i] = acc;
			}
			historico = buf.slice(buf.length - FIR_48K.length);
			return saida;
		}
		// Outras taxas (44,1 kHz): média da janela, o bastante como reserva.
		const razao = taxaOrigem / TAXA_ENVIO;
		const tamanho = Math.floor(entrada.length / razao);
		const saida = new Float32Array(tamanho);
		for (let i = 0; i < tamanho; i += 1) {
			const ini = Math.floor(i * razao);
			const fim = Math.min(entrada.length, Math.floor((i + 1) * razao));
			let soma = 0;
			for (let j = ini; j < fim; j += 1) soma += entrada[j];
			saida[i] = soma / Math.max(1, fim - ini);
		}
		return saida;
	};
};

const paraInt16 = (f32) => {
	const out = new Int16Array(f32.length);
	for (let i = 0; i < f32.length; i += 1) {
		const s = Math.max(-1, Math.min(1, f32[i]));
		out[i] = s < 0 ? s * 32768 : s * 32767;
	}
	return out;
};

const pico = (f32) => {
	let p = 0;
	for (let i = 0; i < f32.length; i += 4) {
		const v = Math.abs(f32[i]);
		if (v > p) p = v;
	}
	return p;
};

// Opus pelo navegador: precisa de AudioEncoder E AudioDecoder aceitando a
// configuração. Qualquer "não" aqui manda para o PCM, que sempre funciona.
// Taxa do encoder = taxa do AudioContext (48 kHz na prática): o microfone
// entra direto no Opus, sem redução de taxa no meio do caminho.
const configEncoder = (taxa) => ({
	codec: "opus",
	sampleRate: taxa,
	numberOfChannels: 1,
	bitrate: 32000,
	opus: { frameDuration: 20000 },
});
const TAXAS_OPUS = [48000, 24000, 16000];
const CONFIG_DECODER = { codec: "opus", sampleRate: TAXA_OPUS_RETORNO, numberOfChannels: 1 };

let suporteOpus = null;
const temOpus = async () => {
	if (suporteOpus !== null) return suporteOpus;
	try {
		if (!window.AudioEncoder || !window.AudioDecoder || !window.AudioData) {
			suporteOpus = false;
			return false;
		}
		const [enc, dec] = await Promise.all([
			window.AudioEncoder.isConfigSupported(configEncoder(48000)),
			window.AudioDecoder.isConfigSupported(CONFIG_DECODER),
		]);
		suporteOpus = Boolean(enc?.supported && dec?.supported);
	} catch {
		suporteOpus = false;
	}
	return suporteOpus;
};

const ligarCaptura = async (ctx, fonte, aoBloco) => {
	if (ctx.audioWorklet) {
		try {
			const url = URL.createObjectURL(new Blob([CODIGO_WORKLET], { type: "application/javascript" }));
			await ctx.audioWorklet.addModule(url);
			URL.revokeObjectURL(url);
			const no = new AudioWorkletNode(ctx, "captura-ligacao", { numberOfInputs: 1, numberOfOutputs: 1 });
			no.port.onmessage = (ev) => aoBloco(ev.data);
			// Ligado ao destino por ganho zero: nó fora do grafo até o destino
			// pode deixar de ser processado, e aí a voz some sem erro nenhum.
			const zero = ctx.createGain();
			zero.gain.value = 0;
			fonte.connect(no);
			no.connect(zero);
			zero.connect(ctx.destination);
			return () => {
				no.port.onmessage = null;
				no.disconnect();
				zero.disconnect();
			};
		} catch {
			// cai no ScriptProcessor abaixo
		}
	}
	const proc = ctx.createScriptProcessor(1024, 1, 1);
	proc.onaudioprocess = (ev) => aoBloco(ev.inputBuffer.getChannelData(0).slice(0));
	const mudo = ctx.createGain();
	mudo.gain.value = 0;
	fonte.connect(proc);
	proc.connect(mudo);
	mudo.connect(ctx.destination);
	return () => {
		proc.onaudioprocess = null;
		proc.disconnect();
		mudo.disconnect();
	};
};

export const abrirAudio = async (id, { microfoneId, saidaId, aoMudarEstado } = {}) => {
	const microfone = await navigator.mediaDevices.getUserMedia({
		audio: {
			...(microfoneId ? { deviceId: { exact: microfoneId } } : {}),
			channelCount: 1,
			echoCancellation: true,
			noiseSuppression: true,
			autoGainControl: true,
		},
	});

	// Pede 48 kHz (a taxa do Opus). Se o navegador não ligar o microfone
	// num contexto com taxa diferente da placa (Firefox antigo), volta ao padrão.
	const Contexto = window.AudioContext || window.webkitAudioContext;
	let ctx;
	let fonte;
	try {
		ctx = new Contexto({ sampleRate: 48000 });
		fonte = ctx.createMediaStreamSource(microfone);
	} catch {
		ctx?.close?.().catch(() => {});
		ctx = new Contexto();
		fonte = ctx.createMediaStreamSource(microfone);
	}
	await ctx.resume().catch(() => {});
	// Saida de audio escolhida (Ligacoes > Este computador). setSinkId no
	// AudioContext existe no Chrome 110+; sem suporte, cai no padrao do sistema.
	if (saidaId && typeof ctx.setSinkId === "function") {
		await ctx.setSinkId(saidaId).catch(() => {});
	}
	const reduzir = criarRedutor(ctx.sampleRate);
	const taxaEncoder = TAXAS_OPUS.includes(ctx.sampleRate) ? ctx.sampleRate : TAXA_ENVIO;

	const querOpus = await temOpus();
	const ws = new WebSocket(urlDoAudio(id), querOpus ? ["opus", "pcm16"] : ["pcm16"]);
	ws.binaryType = "arraybuffer";

	let mudo = false;
	let cursor = 0;
	let fechado = false;
	let nivelLocal = 0;
	let modo = null;
	let encoder = null;
	let decoder = null;
	let pendente = new Float32Array(0);
	let tsEnvio = 0;
	let tsRetorno = 0;
	let desligarCaptura = () => {};

	const analisador = ctx.createAnalyser();
	analisador.fftSize = 256;
	analisador.connect(ctx.destination);

	const tocar = (f32, taxa) => {
		if (!f32.length) return;
		const ab = ctx.createBuffer(1, f32.length, taxa);
		ab.copyToChannel(f32, 0);
		const src = ctx.createBufferSource();
		src.buffer = ab;
		src.connect(analisador);
		const agora = ctx.currentTime;
		// Atrasou (rede engasgou) → reancora com folga; senão encadeia sem buraco.
		if (cursor < agora + 0.005) cursor = agora + FOLGA_INICIAL;
		// Atraso acumulado demais (> 400 ms) vira eco de conversa: descarta.
		if (cursor > agora + 0.4) cursor = agora + FOLGA_INICIAL;
		src.start(cursor);
		cursor += ab.duration;
	};

	const enviarPcm = (quadro) => {
		if (ws.readyState === WebSocket.OPEN) ws.send(paraInt16(quadro).buffer);
	};

	const codificar = (amostras, taxa) => {
		if (encoder?.state !== "configured" || !amostras.length) return;
		const dados = new window.AudioData({
			format: "f32",
			sampleRate: taxa,
			numberOfFrames: amostras.length,
			numberOfChannels: 1,
			timestamp: tsEnvio,
			data: amostras,
		});
		tsEnvio += (amostras.length / taxa) * 1e6;
		encoder.encode(dados);
		dados.close();
	};

	const aoBloco = (bloco) => {
		nivelLocal = mudo ? 0 : pico(bloco);
		const entrada = mudo ? new Float32Array(bloco.length) : bloco;
		if (modo === "opus") {
			// O AudioEncoder monta os quadros de 20 ms sozinho; na taxa nativa
			// vai direto, sem redução.
			if (taxaEncoder === ctx.sampleRate) codificar(entrada, ctx.sampleRate);
			else codificar(reduzir(entrada), TAXA_ENVIO);
			return;
		}
		if (modo !== "pcm16") return;
		const reduzido = reduzir(entrada);
		const junto = new Float32Array(pendente.length + reduzido.length);
		junto.set(pendente);
		junto.set(reduzido, pendente.length);
		let i = 0;
		for (; i + AMOSTRAS_QUADRO <= junto.length; i += AMOSTRAS_QUADRO) {
			enviarPcm(junto.subarray(i, i + AMOSTRAS_QUADRO));
		}
		pendente = junto.slice(i);
	};

	const encerrar = () => {
		if (fechado) return;
		fechado = true;
		desligarCaptura();
		microfone.getTracks().forEach((t) => t.stop());
		try {
			if (encoder && encoder.state !== "closed") encoder.close();
			if (decoder && decoder.state !== "closed") decoder.close();
		} catch {
			// já fechado
		}
		ctx.close().catch(() => {});
		if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
			ws.close(1000, "fim");
		}
		aoMudarEstado?.("fechado");
	};

	await new Promise((resolve, reject) => {
		ws.onopen = () => {
			modo = ws.protocol === "opus" ? "opus" : "pcm16";
			if (modo === "opus") {
				encoder = new window.AudioEncoder({
					output: (chunk) => {
						const bytes = new Uint8Array(chunk.byteLength);
						chunk.copyTo(bytes);
						if (ws.readyState === WebSocket.OPEN) ws.send(bytes.buffer);
					},
					error: () => {
						// encoder quebrou no meio: segue sem enviar voz, mas não derruba a ligação
					},
				});
				encoder.configure(configEncoder(taxaEncoder));
				decoder = new window.AudioDecoder({
					output: (dados) => {
						const f32 = new Float32Array(dados.numberOfFrames);
						dados.copyTo(f32, { planeIndex: 0, format: "f32-planar" });
						tocar(f32, dados.sampleRate);
						dados.close();
					},
					error: () => {},
				});
				decoder.configure(CONFIG_DECODER);
			}
			aoMudarEstado?.("conectado");
			resolve();
		};
		ws.onerror = () => reject(new Error("Não foi possível abrir o áudio da ligação."));
		ws.onclose = () => encerrar();
		ws.onmessage = (ev) => {
			if (!(ev.data instanceof ArrayBuffer)) return;
			if (modo === "opus") {
				if (decoder?.state !== "configured") return;
				decoder.decode(
					new window.EncodedAudioChunk({ type: "key", timestamp: tsRetorno, data: ev.data })
				);
				tsRetorno += 20000;
				return;
			}
			const int16 = new Int16Array(ev.data);
			const f32 = new Float32Array(int16.length);
			for (let i = 0; i < int16.length; i += 1) f32[i] = int16[i] / 32768;
			tocar(f32, TAXA_ENVIO);
		};
	}).catch((err) => {
		encerrar();
		throw err;
	});

	desligarCaptura = await ligarCaptura(ctx, fonte, aoBloco);

	return {
		modo: () => modo,
		definirMudo: (v) => {
			mudo = Boolean(v);
		},
		nivelLocal: () => nivelLocal,
		nivelRemoto: () => {
			const dados = new Uint8Array(analisador.frequencyBinCount);
			analisador.getByteTimeDomainData(dados);
			let p = 0;
			for (let i = 0; i < dados.length; i += 1) p = Math.max(p, Math.abs(dados[i] - 128));
			return p / 128;
		},
		encerrar,
	};
};

// Toque de chamada gerado na hora (sem arquivo de áudio): dois bipes a cada
// 2 s, no volume de alertas escolhido pela pessoa.
export const criarToque = () => {
	let ctx = null;
	let timer = null;

	const bipe = (volume) => {
		const agora = ctx.currentTime;
		[0, 0.25].forEach((atraso) => {
			const osc = ctx.createOscillator();
			const ganho = ctx.createGain();
			osc.frequency.value = 480;
			ganho.gain.setValueAtTime(0, agora + atraso);
			ganho.gain.linearRampToValueAtTime(0.25 * volume, agora + atraso + 0.02);
			ganho.gain.linearRampToValueAtTime(0, agora + atraso + 0.2);
			osc.connect(ganho);
			ganho.connect(ctx.destination);
			osc.start(agora + atraso);
			osc.stop(agora + atraso + 0.22);
		});
	};

	return {
		iniciar: (volume = 1) => {
			if (timer) return;
			try {
				ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
				ctx.resume().catch(() => {});
				bipe(volume);
				timer = setInterval(() => bipe(volume), 2000);
			} catch {
				// navegador sem Web Audio: fica só o aviso visual
			}
		},
		parar: () => {
			if (timer) clearInterval(timer);
			timer = null;
		},
	};
};
